import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Send, Bell } from "lucide-react";
import { toast } from "sonner";
import { adminGetSettings, adminUpdateSettings, adminTestTelegram } from "@/lib/api";

export const NotificationSettings = () => {
  const [enabled, setEnabled] = useState(false);
  const [chatId, setChatId] = useState("");
  const [botToken, setBotToken] = useState("");
  const [configured, setConfigured] = useState(false);
  const [masked, setMasked] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    adminGetSettings()
      .then((s) => {
        const t = s.telegram || {};
        setEnabled(!!t.enabled);
        setChatId(t.chatId || "");
        setConfigured(!!t.configured);
        setMasked(t.botTokenMasked || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { telegram: { enabled, chatId } };
      if (botToken.trim()) payload.telegram.botToken = botToken.trim();
      const s = await adminUpdateSettings(payload);
      const t = s.telegram || {};
      setConfigured(!!t.configured);
      setMasked(t.botTokenMasked || "");
      setBotToken("");
      toast.success("Na-save ang notification settings");
    } catch (e) {
      toast.error("Hindi na-save");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      await adminTestTelegram();
      toast.success("Naipadala ang test message! Check mo ang Telegram.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hindi naipadala ang test");
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-sm text-muted-foreground">Naglo-load...</div>;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Telegram Notifications</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Makatanggap ng instant alert sa Telegram tuwing may bagong Team Lead o Order.</p>
      </div>

      <Card className="space-y-4 rounded-xl border-border bg-card p-4">
        <div className="flex items-center gap-3">
          <Switch checked={enabled} onCheckedChange={setEnabled} id="tg-enabled" data-testid="settings-telegram-enabled" />
          <Label htmlFor="tg-enabled" className="text-sm">I-enable ang Telegram alerts</Label>
        </div>
        <div>
          <Label className="text-xs font-medium text-foreground">
            Bot Token {configured ? <span className="ml-1 text-primary">(naka-set na: {masked})</span> : null}
          </Label>
          <Input className="mt-1 h-10" type="password" value={botToken} onChange={(e) => setBotToken(e.target.value)} placeholder={configured ? "Iwan blangko para hindi baguhin" : "123456:ABC-DEF..."} data-testid="settings-telegram-token" />
        </div>
        <div>
          <Label className="text-xs font-medium text-foreground">Chat ID</Label>
          <Input className="mt-1 h-10" value={chatId} onChange={(e) => setChatId(e.target.value)} placeholder="hal. 123456789" data-testid="settings-telegram-chatid" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving} data-testid="settings-save-button">
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sina-save...</> : <><Save className="mr-2 h-4 w-4" /> I-save</>}
          </Button>
          <Button variant="outline" onClick={test} disabled={testing} data-testid="settings-test-button">
            {testing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sinusubok...</> : <><Send className="mr-2 h-4 w-4" /> Send test</>}
          </Button>
        </div>
      </Card>

      <Card className="rounded-xl border-border bg-card p-4 text-sm text-muted-foreground">
        <p className="font-semibold text-foreground">Paano kumuha ng Bot Token at Chat ID:</p>
        <ol className="mt-2 list-decimal space-y-1 pl-5">
          <li>Sa Telegram, hanapin si <span className="font-medium text-foreground">@BotFather</span> → i-send ang <span className="font-mono">/newbot</span> → sundin ang steps → kopyahin ang <span className="font-medium text-foreground">Bot Token</span>.</li>
          <li>I-message ang bagong bot mo (kahit "hi" lang) para ma-activate ito.</li>
          <li>Para sa <span className="font-medium text-foreground">Chat ID</span>: i-message si <span className="font-medium text-foreground">@userinfobot</span> — ibibigay niya ang numeric Chat ID mo.</li>
          <li>I-paste dito, i-save, tapos i-click ang <span className="font-medium text-foreground">Send test</span>.</li>
        </ol>
      </Card>
    </div>
  );
};
